'use client'

import GradientText from './GradientText'
import { trackEvent } from '../utils/analytics'

type HeaderProps = {
  ctaText?: string
  ctaLink?: string
  showBackArrow?: boolean
}

export default function Header({ ctaText = 'Sign Up for Free', ctaLink = 'https://app.venturecompass.ai/signup', showBackArrow = false }: HeaderProps = {}) {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out">
      {/* Align with hero frame: p-2 sm:p-4 matches hero section padding */}
      <div className="px-2 sm:px-4 py-4 sm:py-6 transition-all duration-500">
        {/* Mobile: Single container with logo and Get a Demo button */}
        <div className="md:hidden">
          <div className="mx-1 glassmorphism-button px-4 py-2 transition-all duration-500 [&::before]:hidden">
            <div className="flex justify-between items-center">
              <a href="/" className="flex items-center focus-ring rounded-lg" onClick={() => trackEvent('logo_click', { location: 'header_mobile' })}>
                <GradientText
                  className="!mx-0 !justify-start"
                  colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}
                >
                  <span className="text-base font-normal">VentureCompass</span>
                </GradientText>
              </a>
              <a
                href={ctaLink}
                className="text-white font-medium transition-all duration-500 focus-ring rounded-lg flex items-center gap-2 text-sm"
                onClick={() => trackEvent('cta_click', { location: 'header_mobile', text: ctaText })}
              >
                {showBackArrow && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                  </svg>
                )}
                {ctaText}
              </a>
            </div>
          </div>
        </div>

        {/* Desktop: Two separate containers */}
        <div className="hidden md:flex justify-between items-center">
          {/* Logo + Menu Items - Left Side */}
          <div className="ml-2 transition-all duration-500 flex items-center glassmorphism-button px-4 py-3 gap-6 [&::before]:hidden">
            <a href="/" className="flex items-center focus-ring rounded-lg" onClick={() => trackEvent('logo_click', { location: 'header_desktop' })}>
              <GradientText
                className="!mx-0 !justify-start"
                colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}
              >
                <span className="text-xl font-normal">VentureCompass</span>
              </GradientText>
            </a>

            {/* Menu Items */}
            <nav aria-label="Main navigation" className="flex items-center gap-4">
              <a
                href="#features"
                className="text-white font-medium px-4 py-2 -mx-1 -my-1 rounded-lg hover:bg-white/10 transition-all duration-200 text-sm focus-ring"
                onClick={() => trackEvent('nav_click', { destination: 'features', location: 'header' })}
              >
                Features
              </a>
              <a
                href="#who-its-for"
                className="text-white font-medium px-4 py-2 -mx-1 -my-1 rounded-lg hover:bg-white/10 transition-all duration-200 text-sm focus-ring"
                onClick={() => trackEvent('nav_click', { destination: 'solutions', location: 'header' })}
              >
                Solutions
              </a>
              <a
                href="#how-it-works"
                className="text-white font-medium px-4 py-2 -mx-1 -my-1 rounded-lg hover:bg-white/10 transition-all duration-200 text-sm focus-ring"
                onClick={() => trackEvent('nav_click', { destination: 'how_it_works', location: 'header' })}
              >
                How It Works
              </a>
              <a
                href="#pricing"
                className="text-white font-medium px-4 py-2 -mx-1 -my-1 rounded-lg hover:bg-white/10 transition-all duration-200 text-sm focus-ring"
                onClick={() => trackEvent('nav_click', { destination: 'pricing', location: 'header' })}
              >
                Pricing
              </a>
            </nav>
          </div>

          {/* CTA Button - Right Side */}
          <a
            href={ctaLink}
            className="glassmorphism-button px-6 py-3 mr-2 transition-all duration-500 text-white font-medium focus-ring rounded-lg inline-flex items-center gap-2 text-base"
            onClick={() => trackEvent('cta_click', { location: 'header_desktop', text: ctaText })}
          >
            {showBackArrow && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
            )}
            {ctaText}
          </a>
        </div>
      </div>
    </header>
  )
}